import apiClient from '../../services/api';
import { DIGISAKA_API_ORIGIN } from '../../config/digisakaApiOrigin';

export interface DroneProfile {
  name: string;
  max_images: number;
  warn_images: number;
  max_zip_bytes: number;
  nodeodm_memory_gb: number;
  nodeodm_cpus: number;
  enabled_alignment: string[];
  allow_dtm: boolean;
  allow_reflectance: boolean;
  allow_dense: boolean;
  skip_dense: boolean;
  disabled_note: string;
  outputs_enabled: string[];
}

export interface DroneFileRef {
  source: 'upload' | 's3_url' | string;
  filename: string | null;
  bytes: number | null;
  key: string | null;
  s3_url?: string | null;
}

export interface DroneJob {
  id: string;
  workspace_id?: string | null;
  name: string;
  farm_id: string | null;
  status: string;
  progress: number;
  stage: string;
  source: string | null;
  inputs: {
    rgb: DroneFileRef | null;
    multispec: DroneFileRef | null;
    gcp: DroneFileRef | null;
    flight_log: DroneFileRef | null;
  };
  options: Record<string, unknown>;
  outputs: {
    orthophoto_key: string | null;
    dsm_key: string | null;
    dtm_key: string | null;
    shots_key: string | null;
  };
  tiles: {
    orthophoto?: string;
    dsm?: string;
    dtm?: string;
  };
  stats: {
    images?: number;
    area_ha?: number;
    gsd_cm?: number;
    cameras_aligned?: number;
    tie_points?: number;
    crs?: string;
  };
  error: string | null;
  warning: string | null;
  created_at: string | null;
  updated_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface DroneOptions {
  alignment_quality: 'low' | 'medium' | 'high';
  keypoint_limit: number;
  tie_point_limit: number;
  dense_quality: 'low' | 'medium' | 'high';
  surface_model: 'dsm' | 'dsm_dtm';
  reflectance: boolean;
}

export const DEFAULT_OPTIONS: DroneOptions = {
  alignment_quality: 'low',
  keypoint_limit: 40000,
  tie_point_limit: 4000,
  dense_quality: 'medium',
  surface_model: 'dsm',
  reflectance: false,
};

export function apiError(err: unknown): string {
  if (typeof err === 'object' && err && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => (typeof item === 'object' && item && 'msg' in item ? String(item.msg) : String(item)))
        .join(', ');
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

export interface WorkspacePerson {
  email: string;
  name: string | null;
  role: string;
}

export interface WorkspaceUsage {
  workspace_id: string;
  name: string;
  is_owner: boolean;
  people: WorkspacePerson[];
  runs_used: number;
  runs_limit: number;
  storage_used: number;
  storage_limit: number;
  analysis_seconds_used?: number;
  analysis_seconds_limit?: number;
}

export interface WorkspaceSummary {
  workspace_id: string;
  name: string;
  role: string;
  is_owner: boolean;
}

export async function fetchWorkspaces(): Promise<WorkspaceSummary[]> {
  const response = await apiClient.get<{ workspaces: WorkspaceSummary[] }>('/drone-processing/workspaces');
  return response.data.workspaces;
}

export async function createWorkspace(name: string): Promise<WorkspaceSummary> {
  const response = await apiClient.post<WorkspaceSummary>('/drone-processing/workspaces', { name });
  return response.data;
}

export async function renameWorkspace(workspaceId: string, name: string): Promise<WorkspaceSummary> {
  const response = await apiClient.patch<WorkspaceSummary>(`/drone-processing/workspaces/${workspaceId}`, { name });
  return response.data;
}

export async function fetchUsage(workspaceId: string): Promise<WorkspaceUsage> {
  const response = await apiClient.get<WorkspaceUsage>('/drone-processing/usage', {
    params: { workspace_id: workspaceId },
  });
  return response.data;
}

export interface AccountLookup {
  email: string;
  exists: boolean;
  name: string | null;
  already_member: boolean;
}

export interface InvitePreview {
  email: string;
  workspace_name: string;
}

export async function lookupWorkspaceAccount(workspaceId: string, email: string): Promise<AccountLookup> {
  const response = await apiClient.get<AccountLookup>(`/drone-processing/workspaces/${workspaceId}/account`, {
    params: { email },
  });
  return response.data;
}

export async function previewWorkspaceInvite(token: string): Promise<InvitePreview> {
  const response = await apiClient.get<InvitePreview>(
    `/drone-processing/workspace-invites/${encodeURIComponent(token)}`
  );
  return response.data;
}

export async function acceptWorkspaceInvite(token: string): Promise<{ workspace_id: string; name: string }> {
  const response = await apiClient.post<{ workspace_id: string; name: string }>(
    `/drone-processing/workspace-invites/${encodeURIComponent(token)}/accept`
  );
  return response.data;
}

export async function inviteWorkspace(workspaceId: string, emails: string[]): Promise<{ sent: string[]; failed: string[] }> {
  const response = await apiClient.post<{ sent: string[]; failed: string[] }>(
    `/drone-processing/workspaces/${workspaceId}/invites`,
    { emails }
  );
  return response.data;
}

export async function fetchProfile(): Promise<DroneProfile> {
  const response = await apiClient.get<DroneProfile>('/drone-processing/profile');
  return response.data;
}

export async function fetchJobs(workspaceId?: string): Promise<DroneJob[]> {
  const response = await apiClient.get<{ jobs: DroneJob[] }>('/drone-processing/jobs', {
    params: workspaceId ? { workspace_id: workspaceId } : undefined,
  });
  return response.data.jobs;
}

export async function fetchJob(id: string): Promise<DroneJob> {
  const response = await apiClient.get<DroneJob>(`/drone-processing/jobs/${id}`);
  return response.data;
}

export async function createJob(name: string, options: DroneOptions, workspaceId: string): Promise<DroneJob> {
  const response = await apiClient.post<DroneJob>('/drone-processing/jobs', {
    name,
    options,
    workspace_id: workspaceId,
  });
  return response.data;
}

export async function attachS3Url(jobId: string, role: 'rgb' | 'multispec', url: string): Promise<DroneJob> {
  const response = await apiClient.post<DroneJob>(`/drone-processing/jobs/${jobId}/s3-url`, { role, url });
  return response.data;
}

export async function clearJobInput(jobId: string, role: 'rgb' | 'multispec' | 'gcp' | 'flight_log'): Promise<void> {
  await apiClient.post(`/drone-processing/jobs/${jobId}/inputs/clear`, { role });
}

export async function uploadSmallFile(
  jobId: string,
  role: 'gcp' | 'flight_log',
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<DroneJob> {
  const body = new FormData();
  body.append('role', role);
  body.append('file', file);
  const response = await apiClient.post<DroneJob>(`/drone-processing/jobs/${jobId}/files`, body, {
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return;
      onProgress(event.loaded, event.total);
    },
  });
  return response.data;
}

export async function uploadZip(
  jobId: string,
  role: 'rgb' | 'multispec',
  file: File,
  onProgress: (fraction: number) => void
): Promise<void> {
  const init = await apiClient.post<{
    upload_id: string;
    part_size: number;
    part_count: number;
  }>(`/drone-processing/jobs/${jobId}/uploads/init`, {
    role,
    filename: file.name,
    size: file.size,
  });
  const { upload_id, part_size, part_count } = init.data;
  const parts: { part_number: number; etag: string }[] = [];
  try {
    for (let index = 0; index < part_count; index += 1) {
      const partNumber = index + 1;
      const start = index * part_size;
      const blob = file.slice(start, Math.min(file.size, start + part_size));
      const presign = await apiClient.post<{ url: string }>(`/drone-processing/jobs/${jobId}/uploads/part`, {
        role,
        upload_id,
        part_number: partNumber,
      });
      const put = await fetch(presign.data.url, { method: 'PUT', body: blob });
      if (!put.ok) {
        throw new Error(`Upload to S3 failed (${put.status}). Check the bucket CORS policy.`);
      }
      const etag = put.headers.get('ETag') || put.headers.get('etag');
      if (!etag) {
        throw new Error('S3 did not expose ETag. Add ETag to the bucket CORS ExposeHeaders list.');
      }
      parts.push({ part_number: partNumber, etag });
      onProgress(partNumber / part_count);
    }
    await apiClient.post(`/drone-processing/jobs/${jobId}/uploads/complete`, {
      role,
      upload_id,
      parts,
    });
  } catch (error) {
    await apiClient.post(`/drone-processing/jobs/${jobId}/uploads/abort`, { role, upload_id }).catch(() => undefined);
    throw error;
  }
}

export interface AlreadyProcessed {
  message: string;
  already: string[];
  fresh: string[];
}

export function alreadyProcessed(err: unknown): AlreadyProcessed | null {
  if (typeof err !== 'object' || !err || !('response' in err)) return null;
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (!detail || typeof detail !== 'object' || !('code' in detail)) return null;
  const body = detail as { code?: string; message?: string; already?: string[]; fresh?: string[] };
  if (body.code !== 'already_processed') return null;
  return {
    message: body.message || 'These photos were already processed. Process again?',
    already: body.already || [],
    fresh: body.fresh || [],
  };
}

export async function startJob(jobId: string, confirm = false): Promise<DroneJob> {
  const response = await apiClient.post<DroneJob>(
    `/drone-processing/jobs/${jobId}/start`,
    confirm ? { confirm: true } : {}
  );
  return response.data;
}

export async function cancelJob(jobId: string): Promise<DroneJob> {
  const response = await apiClient.post<DroneJob>(`/drone-processing/jobs/${jobId}/cancel`);
  return response.data;
}

export async function deleteJob(jobId: string): Promise<void> {
  await apiClient.delete(`/drone-processing/jobs/${jobId}`);
}

export async function fetchShots(jobId: string): Promise<{ type: 'FeatureCollection'; features: object[] }> {
  const response = await apiClient.get<{ type: 'FeatureCollection'; features: object[] }>(
    `/drone-processing/jobs/${jobId}/shots`
  );
  return response.data;
}

export type DroneRaster = 'ortho' | 'dsm' | 'dtm' | 'ndvi' | 'ndwi' | 'ndmi';

export type DroneIndex = 'ndvi' | 'ndwi' | 'ndmi';

export type DroneIndexOption = {
  key: DroneIndex;
  label: string;
  /** Band subtracted in the numerator: (a − b) / (a + b). */
  a: number;
  b: number;
  hint: string;
  colormap: string;
};

const DRONE_INDEX_DEFS: {
  key: DroneIndex;
  label: string;
  a: string;
  b: string;
  hint: string;
  needs: string;
  colormap: string;
}[] = [
  {
    key: 'ndvi',
    label: 'NDVI',
    a: 'NIR',
    b: 'Red',
    hint: 'NDVI from NIR and Red. Green is healthy plants.',
    needs: 'Needs NIR and Red',
    colormap: 'rdylgn',
  },
  {
    key: 'ndwi',
    label: 'NDWI',
    a: 'Green',
    b: 'NIR',
    hint: 'NDWI from Green and NIR. Blue is water.',
    needs: 'Needs Green and NIR',
    colormap: 'rdylbu',
  },
  {
    key: 'ndmi',
    label: 'NDMI',
    a: 'NIR',
    b: 'SWIR',
    hint: 'NDMI from NIR and SWIR. Blue is a wetter canopy.',
    needs: 'Needs NIR and SWIR',
    colormap: 'rdylbu',
  },
];

export function isDroneIndex(kind: DroneRaster): kind is DroneIndex {
  return kind === 'ndvi' || kind === 'ndwi' || kind === 'ndmi';
}

function namedBand(bands: CogBand[], name: string): number | undefined {
  if (name === 'SWIR') {
    return bands.find((band) => /^swir\d*$/i.test(band.name.replace(/\s+/g, '')))?.index;
  }
  return bands.find((band) => band.name === name)?.index;
}

/** Indexes this orthomosaic can draw. Each one needs both of its bands. */
export function availableDroneIndexes(bands: CogBand[]): DroneIndexOption[] {
  return DRONE_INDEX_DEFS.flatMap((def) => {
    const a = namedBand(bands, def.a);
    const b = namedBand(bands, def.b);
    if (a == null || b == null || a === b) return [];
    return [{ key: def.key, label: def.label, a, b, hint: def.hint, colormap: def.colormap }];
  });
}

export function droneIndexNeeds(key: DroneIndex): string {
  return DRONE_INDEX_DEFS.find((def) => def.key === key)?.needs ?? '';
}

export function tileUrl(
  presigned: string,
  kind: DroneRaster,
  index?: Pick<DroneIndexOption, 'a' | 'b' | 'colormap'>
): string {
  if (isDroneIndex(kind) && index) {
    const params = new URLSearchParams();
    params.set('url', presigned);
    // uint16 bands wrap if the subtraction stays in integer math, which paints a blank tile.
    params.set('expression', `(b${index.a}*1.0-b${index.b}*1.0)/(b${index.a}*1.0+b${index.b}*1.0)`);
    params.set('rescale', '-1,1');
    params.set('colormap_name', index.colormap);
    params.set('resampling', 'bilinear');
    return `${DIGISAKA_API_ORIGIN}/api/tiles/WebMercatorQuad/{z}/{x}/{y}.png?${params.toString()}`;
  }
  const params =
    kind === 'ortho'
      ? `url=${encodeURIComponent(presigned)}&bidx=1&bidx=2&bidx=3&resampling=bilinear`
      : `url=${encodeURIComponent(presigned)}&bidx=1&colormap_name=terrain&nodata=-9999&resampling=bilinear`;
  return `${DIGISAKA_API_ORIGIN}/api/tiles/WebMercatorQuad/{z}/{x}/{y}.png?${params}`;
}

export function boundsUrl(presigned: string): string {
  return `${DIGISAKA_API_ORIGIN}/api/bounds?url=${encodeURIComponent(presigned)}`;
}

/** TiTiler dataset info for a presigned COG. Public, same as the tile routes. */
export function infoUrl(presigned: string): string {
  return `${DIGISAKA_API_ORIGIN}/api/info?url=${encodeURIComponent(presigned)}`;
}

export interface CogBand {
  index: number;
  name: string;
}

export interface CogBandInfo {
  count: number;
  dtype: string;
  bands: CogBand[];
}

const COLOR_NAMES: Record<string, string> = {
  red: 'Red',
  green: 'Green',
  blue: 'Blue',
  alpha: 'Alpha',
  gray: 'Gray',
  grey: 'Gray',
  palette: 'Palette',
};

function prettyBandName(value: string): string {
  const key = value.toLowerCase().replace(/[_-]+/g, ' ').trim();
  if (key === 'nir' || key === 'near infrared' || key.includes('nir')) return 'NIR';
  if (key.includes('red edge') || key === 'rededge') return 'Red edge';
  if (key === 'red') return 'Red';
  if (key === 'green') return 'Green';
  if (key === 'blue') return 'Blue';
  if (key === 'alpha') return 'Alpha';
  return value;
}

/** Read band names from a TiTiler `/info` body. Descriptions win over color interpretation. */
export function parseCogInfo(body: unknown): CogBandInfo | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as {
    count?: number;
    dtype?: string;
    colorinterp?: string[];
    band_descriptions?: [string, string][];
  };
  const count = typeof raw.count === 'number' ? raw.count : raw.colorinterp?.length ?? 0;
  if (!count) return null;
  const bands = Array.from({ length: count }, (_, index) => {
    const description = raw.band_descriptions?.[index]?.[1]?.trim() ?? '';
    const color = raw.colorinterp?.[index]?.toLowerCase() ?? '';
    const fromDescription =
      description && !/^b(and)?\s*\d+$/i.test(description) ? prettyBandName(description) : '';
    const fromColor = color && color !== 'undefined' ? COLOR_NAMES[color] || prettyBandName(color) : '';
    return { index: index + 1, name: fromDescription || fromColor || `Band ${index + 1}` };
  });
  return { count, dtype: typeof raw.dtype === 'string' ? raw.dtype : '', bands };
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
